import { getEnvironmentConfig } from '../config/environment';
import { removeNullFields } from '../utils/jsonUtils';

class WorkflowService {
  private apiUrl: string;
  private apiKey: string;
  private accessToken?: string;

  constructor(environment: 'test' | 'production' = 'test', accessToken?: string) {
    const config = getEnvironmentConfig(environment);
    this.apiUrl = config.apiUrl;
    this.apiKey = config.apiKey;
    this.accessToken = accessToken;
  }

  private async handleRequest(endpoint: string, options: RequestInit): Promise<any> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add any additional headers from options
      if (options.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (typeof value === 'string') {
            headers[key] = value;
          }
        });
      }

      // Use Auth0 token if available, otherwise use API key
      if (this.accessToken) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
      } else {
        headers['x-api-key'] = this.apiKey;
      }

      const response = await fetch(endpoint, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Workflow methods
  async executeWorkflow(workflowId: string, input: any): Promise<any> {
    const cleanInput = removeNullFields(input);
    return this.handleRequest(`${this.apiUrl}/workflows/api/execute/${workflowId}`, {
      method: 'POST',
      body: JSON.stringify(cleanInput),
    });
  }

  async executeWorkflowJson(workflow: any): Promise<any> {
    const cleanWorkflow = removeNullFields(workflow);
    return this.handleRequest(`${this.apiUrl}/workflows/api/json/execute`, {
      method: 'POST',
      body: JSON.stringify(cleanWorkflow),
    });
  }

  async saveWorkflow(workflow: any): Promise<any> {
    const cleanWorkflow = removeNullFields(workflow);
    return this.handleRequest(`${this.apiUrl}/workflows/api/create`, {
      method: 'POST',
      body: JSON.stringify(cleanWorkflow),
    });
  }

  async updateWorkflow(workflowId: string, workflow: any): Promise<any> {
    const cleanWorkflow = removeNullFields(workflow);
    return this.handleRequest(`${this.apiUrl}/workflows/api/update/${workflowId}`, {
      method: 'PUT',
      body: JSON.stringify(cleanWorkflow),
    });
  }

  async deleteWorkflow(workflowId: string): Promise<any> {
    return this.handleRequest(`${this.apiUrl}/workflows/api/delete/${workflowId}`, {
      method: 'DELETE',
    });
  }

  async listWorkflows(): Promise<any> {
    return this.handleRequest(`${this.apiUrl}/workflows/api/list`, {
      method: 'GET',
    });
  }

  async getWorkflow(workflowId: string): Promise<any> {
    return this.handleRequest(`${this.apiUrl}/workflows/api/${workflowId}`, {
      method: 'GET',
    });
  }

  async updateWorkflowStatus(workflowId: string, isEnabled: boolean): Promise<any> {
    return this.handleRequest(`${this.apiUrl}/workflows/api/toggle/${workflowId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        is_enabled: isEnabled
      }),
    });
  }

  // Agent methods
  async listAgents(): Promise<any> {
    return this.handleRequest(`${this.apiUrl}/workflows/agents/api/list`, {
      method: 'GET',
    });
  }

  async getAgentDetails(agentId: string): Promise<any> {
    return this.handleRequest(`${this.apiUrl}/workflows/agents/api/${agentId}`, {
      method: 'GET',
    });
  }

  async createAgent(agent: any): Promise<any> {
    const cleanAgent = removeNullFields(agent);
    return this.handleRequest(`${this.apiUrl}/workflows/agents/api/create`, {
      method: 'POST',
      body: JSON.stringify(cleanAgent),
    });
  }

  async updateAgent(agentId: string, agent: any): Promise<any> {
    const cleanAgent = removeNullFields(agent);
    return this.handleRequest(`${this.apiUrl}/workflows/agents/api/update/${agentId}`, {
      method: 'PUT',
      body: JSON.stringify(cleanAgent),
    });
  }

  async toggleAgentStatus(agentId: string, isEnabled: boolean): Promise<any> {
    return this.handleRequest(`${this.apiUrl}/workflows/agents/api/toggle/${agentId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        is_enabled: isEnabled
      }),
    });
  }

  async deleteAgent(agentId: string): Promise<any> {
    return this.handleRequest(`${this.apiUrl}/workflows/agents/api/delete/${agentId}`, {
      method: 'DELETE',
    });
  }

  async executeAgent(agentId: string, input: any): Promise<any> {
    const cleanInput = removeNullFields(input);
    return this.handleRequest(`${this.apiUrl}/workflows/agents/api/execute/${agentId}`, {
      method: 'POST',
      body: JSON.stringify(cleanInput),
    });
  }
}

export default WorkflowService;